// Iter 48 — SSE listener hook for the Triage inbox.
//
// One EventSource per page (auto-reconnects on transient drops). Calls the
// supplied `onEvent` whenever the server pushes an `email.new`,
// `email.updated`, or `email.deleted` event so the caller can patch its
// local state in place — no list re-fetch required.
import { useEffect, useRef } from "react";

export default function useInboxSSE(apiBase, onEvent, { enabled = true } = {}) {
  // useRef so the same handler is callable from inside the EventSource
  // closures without retriggering the effect on every parent render.
  const handlerRef = useRef(onEvent);
  useEffect(() => { handlerRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!enabled || !apiBase) return undefined;
    // The session cookie is httpOnly = false in our config, so EventSource
    // (which doesn't let us set headers) still works because the browser
    // attaches cookies automatically when the URL is same-origin.
    // `withCredentials` ensures cookies are sent for cross-subdomain too.
    const es = new EventSource(`${apiBase}/api/stream/inbox`, { withCredentials: true });

    const dispatch = (eventType) => (e) => {
      try {
        const payload = JSON.parse(e.data || "{}");
        const evt = { ...payload, type: payload.type || eventType };
        handlerRef.current?.(evt);
        // Iter 110 — Mirror every server-pushed event onto a window
        // CustomEvent so peripheral UI (e.g. the "Live · just now" pill
        // on the Gmail Connected card) can react without each subscriber
        // having to open its own EventSource.
        try {
          window.dispatchEvent(new CustomEvent("close:inbox-event", { detail: evt }));
        } catch (_e) { /* SSR / older browsers */ }
      } catch (err) {
        // Swallow — malformed event shouldn't crash the page.
        console.warn("[sse] bad payload", err);
      }
    };
    es.addEventListener("email.new", dispatch("email.new"));
    es.addEventListener("email.updated", dispatch("email.updated"));
    es.addEventListener("email.deleted", dispatch("email.deleted"));
    es.addEventListener("hello", () => {/* connection ack */});
    es.onerror = () => {
      // Browser auto-reconnects after ~3s; nothing to do here.
    };
    return () => es.close();
  }, [apiBase, enabled]);
}
